import {MealFile, ChartData, DescriptionData, MealCategory} from '@/types/data';
import {Selection as D3Selection} from 'd3';
import * as d3 from 'd3';
import {Constants as constants} from '../config/constants';
import {PathRenderer} from './pathRenderer';
import {CategoryParts} from './categoryPartsRenderer';
import {ArcLabelRenderer} from './arcLabelRenderer';
import {DishesDescriptionRenderer} from './dishesDescriptionRenderer';
import {LegendRenderer} from './legendRenderer';
import {Helpers} from '../common/helpers';
import {Cache as cache} from '../config/cache';
import {LogoRenderer} from './logoRenderer';
import '../../styles/chart.css';

export class Chart {
    private data = {} as ChartData;
    private descriptionData = {} as DescriptionData;
    private svg = null as unknown as D3Selection<any, any, any, any>;
    private container = null as unknown as D3Selection<any, any, any, any>;
    private controller = null as unknown as D3Selection<any, any, any, any>;
    private chart = null as unknown as D3Selection<any, any, any, any>;
    private description = null as unknown as D3Selection<any, any, any, any>;
    private tools = {
        pathRenderer: null as unknown as PathRenderer,
        categoryPartRenderer: null as unknown as CategoryParts,
        arcLabelRenderer: null as unknown as ArcLabelRenderer,
        legendRenderer: null as unknown as LegendRenderer,
        dishesDescriptionRenderer: null as unknown as DishesDescriptionRenderer,
        logoRenderer: null as unknown as LogoRenderer
    };

    constructor(private dataFiles: MealFile[], private dueDate: string, private fileName: string) {
        this.initChart().then(() => {
            this.initTools();
            this.tools.pathRenderer.render();
            this.tools.categoryPartRenderer.render();
            this.tools.arcLabelRenderer.render();
            this.tools.legendRenderer.render();
            this.tools.dishesDescriptionRenderer.render();
            this.tools.logoRenderer.render();

            this.alignGroups();
            this.setCache();
            this.setContainerTop();
        });
    }

    public dispose() {
        this.svg.remove();
    }

    private processDataFiles(data: MealFile[]) {
        const categoryEntries = {
            breakfast: 0,
            lunch: 0,
            supper: 0
        } as {
            [index: string]: number
        };

        data.map((file) => {
            const categoryName = file.category + (typeof categoryEntries[file.category] !== 'undefined' ? `-${categoryEntries[file.category]}` : '');

            if (typeof this.data[categoryName] === 'undefined') {
                this.data[categoryName] = {};
            }

            file = file.default ? file.default : file;
            if (file.percentages) {
                delete file.percentages.id;

                for (const type in file.percentages) {
                    if (file.percentages.hasOwnProperty(type)) {
                        this.data[categoryName][type] = {
                            percentage: file.percentages[type]
                        };
                    }
                }
            }

            this.descriptionData[categoryName] = {
                ingredients: [],
                prepSteps: [],
                name: file.name,
                category: file.category
            };

            for (const ingredientKey in file.ingredients) {
                if (file.ingredients.hasOwnProperty(ingredientKey)) {
                    this.descriptionData[categoryName].ingredients.push(file.ingredients[ingredientKey].food_name.split(',').shift()!);
                }
            }

            for (const key in file) {
                if (Helpers.checkPropIsPrepStep(key)) {
                    file[key] && this.descriptionData[categoryName].prepSteps.push(file[key]);
                }
            }

            categoryEntries[file.category]++;
        });
    }

    private initializeContainers() {
        if (!this.container) {
            // this.svg = d3.select(constants.svgParentSelector)
            this.svg = d3.select('#chart')
                .append('div')
                // Container class to make it responsive.
                .classed('svg-container', true)
                .append('svg')
                .attr('id', constants.svgId)
                .attr('preserveAspectRatio', 'xMinYMin meet')
                .attr('viewBox', '0 0 1500 1500')
                .classed('svg-content-responsive', true);
        }

        if (!this.container) {
            this.container = this.svg
                .append('g');
        }

        if (!this.controller) {
            this.controller = this.container
                .append('g');
        }

        if (!this.chart) {
            this.chart = this.controller
                .append('g');
        }

        if (!this.description) {
            this.description = this.container
                .append('g');
        }
    }

    private initTools() {
        this.tools.pathRenderer = new PathRenderer(this);
        this.tools.categoryPartRenderer = new CategoryParts(this, this.data);
        this.tools.arcLabelRenderer = new ArcLabelRenderer(this);
        this.tools.legendRenderer = new LegendRenderer(this, this.data);
        this.tools.dishesDescriptionRenderer = new DishesDescriptionRenderer(this, this.descriptionData, this.dueDate);
        this.tools.logoRenderer = new LogoRenderer(this);
    }

    private initChart() {
        return new Promise((resolve) => {
            this.initializeContainers();
            this.processDataFiles(this.dataFiles);

            // Delay to be sure that SVG has all props set up
            setTimeout(() => {
                resolve();
            }, 150);
        });
    }

    private alignGroups() {
        const topLeft = Helpers.clientToSvgPoint(
            this.svg.node() as SVGSVGElement,
            this.controller.node() as SVGGElement,
            {
                x: 100,
                y: 0
            }
        );
        const bottomRight = Helpers.clientToSvgPoint(
            this.svg.node() as SVGSVGElement,
            this.controller.node() as SVGGElement,
            {
                x: window.innerWidth,
                y: window.innerHeight
            }
        );
        const descriptionSize = (this.description.node() as SVGGElement).getBBox();

        const transformDiffX = descriptionSize.width / 2;
        this.controller
            .attr('transform', `translate(${transformDiffX}, 0)`);

        const chartSize = (this.controller.node() as SVGGElement).getBBox();

        const transformDescription = {
            x: chartSize.x - descriptionSize.width / 2 * constants.descriptionScaleFactor - constants.chartDescSpace,
            y: this.tools.pathRenderer.data.center.y - descriptionSize.height / 2 * constants.descriptionScaleFactor
        };
        this.description
            .attr('transform', `translate(${transformDescription.x}, ${transformDescription.y}) scale(${constants.descriptionScaleFactor})`);
    }

    private setCache() {
        const viewBoxRect = (this.container.node() as SVGGElement).getBoundingClientRect();

        cache.chartViewBox = {
            x: viewBoxRect.left,
            y: viewBoxRect.top,
            width: viewBoxRect.right - viewBoxRect.left,
            height: viewBoxRect.bottom - viewBoxRect.top
        };
    }

    private setContainerTop() {
        const coords = (this.svg.node() as SVGGElement).getBBox();
        this.container.attr('transform', `translate(0, ${-coords.y})`);
    }

    public get node() {
        return {
            svg: this.svg,
            group: this.controller,
            chart: this.chart,
            description: this.description
        };
    }

    public get toolList() {
        return this.tools;
    }
}
